import type { MetaFunction } from 'react-router';

import { getPublicPrerenderPaths } from '../public-content-manifest.ts';
import { productionOrigin, publicOrganization } from '../public-identity.ts';

const defaultDescription = publicOrganization.description;

const canonicalPublicPaths = new Set(getPublicPrerenderPaths());

function normalizePath(pathname: string): string {
  const path = new URL(pathname, productionOrigin).pathname;
  return path === '/' ? path : path.replace(/\/+$/, '');
}

export function getPublicCanonicalUrl(pathname: string): string | undefined {
  const path = normalizePath(pathname);
  return canonicalPublicPaths.has(path)
    ? `${productionOrigin}${path}`
    : undefined;
}

export function getOpenGraphType(pathname: string): 'article' | 'website' {
  return normalizePath(pathname).startsWith('/blog/') ? 'article' : 'website';
}

export function getPublicMetaDescriptors(
  title: string,
  description: string,
  pathname: string,
) {
  const canonical = getPublicCanonicalUrl(pathname);

  return [
    { title },
    { name: 'description', content: description },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    ...(canonical
      ? [
          { tagName: 'link' as const, rel: 'canonical', href: canonical },
          { property: 'og:url', content: canonical },
          { property: 'og:type', content: getOpenGraphType(pathname) },
        ]
      : []),
  ];
}

export function publicMeta(
  title: string,
  description: string = defaultDescription,
): MetaFunction {
  return ({ location }) =>
    getPublicMetaDescriptors(title, description, location.pathname);
}
